import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const sections = [
  {
    title: '1. Acceptance of Terms',
    body: 'By accessing and using the Azura Mobility website and any associated services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our platform.',
  },
  {
    title: '2. About Azura Mobility',
    body: 'Azura Mobility Limited is a Nigerian electric vehicle operations company incorporated in 2026 and headquartered in Ibadan, Oyo State, Nigeria. The company operates electric fleet services, Battery-as-a-Service (BaaS) infrastructure, and related programmes across Nigeria and targeted African markets.',
  },
  {
    title: '3. Use of This Platform',
    body: 'This website is provided for informational and investor relations purposes. You may not reproduce, redistribute, or exploit any content from this site without prior written permission from Azura Mobility. All intellectual property, including but not limited to brand assets, financial models, and whitepaper content, remains the property of Azura Mobility Limited.',
  },
  {
    title: '4. Investment Disclaimer',
    body: 'Nothing on this website constitutes financial advice, an offer to sell securities, or a solicitation to buy securities. All financial projections, revenue forecasts, and investment-related content are forward-looking statements subject to material risks and uncertainties. Participation in any investment round, including the AZT Token round, carries risk. You should seek independent financial and legal advice before making any investment decision.',
  },
  {
    title: '5. AZT Token',
    body: 'The Azura Token (AZT) is a utility token designed for ecosystem participation within the Azura platform. AZT is not a security, share, or equity instrument. Token purchases do not confer ownership rights in Azura Mobility Limited. The token\'s value is not guaranteed and may decrease to zero. Participation in any token round is subject to eligibility, applicable laws in your jurisdiction, and Azura\'s KYC/AML requirements.',
  },
  {
    title: '6. Privacy & Data',
    body: 'Any personal information submitted through this website (including via the newsletter, investor request forms, or contact forms) is collected and processed in accordance with applicable data protection laws. We use your data solely to respond to your enquiries, send requested communications, and fulfil our obligations. We do not sell personal data to third parties. You may withdraw consent or request deletion of your data at any time by contacting info@azuramobility.com.',
  },
  {
    title: '7. Limitation of Liability',
    body: 'To the fullest extent permitted by applicable law, Azura Mobility Limited shall not be liable for any indirect, incidental, or consequential damages arising from your use of this website or reliance on information contained herein. The content on this site is provided "as is" without warranty of any kind.',
  },
  {
    title: '8. Governing Law',
    body: 'These Terms and Conditions are governed by and construed in accordance with the laws of the Federal Republic of Nigeria. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the courts of Nigeria.',
  },
  {
    title: '9. Changes to These Terms',
    body: 'Azura Mobility reserves the right to update these Terms and Conditions at any time. Continued use of the website following any changes constitutes your acceptance of the revised terms. The date of the most recent revision is indicated at the bottom of this page.',
  },
  {
    title: '10. Contact',
    body: 'For questions regarding these terms, please contact us at info@azuramobility.com or write to: Azura Mobility Limited, Ibadan, Oyo State, Nigeria.',
  },
];

const TermsConditions: React.FC = () => (
  <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

    {/* ══ HERO ══ */}
    <section className="relative bg-bg-dark overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
          <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
          <span className="text-white/70">/</span>
          <span className="text-accent-amber text-xs font-semibold">Terms & Conditions</span>
        </div>

        <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
          Terms &amp; <span className="text-accent-amber">Conditions</span>
        </h1>

        <p className="max-w-2xl text-white/70 text-base leading-8">
          Please read these terms carefully before using the Azura Mobility platform or engaging with any of our investment programmes.
        </p>

        <p className="mt-4 text-xs text-white/70 uppercase tracking-widest">
          Last updated: July 2026 · Azura Mobility Limited
        </p>
      </div>
    </section>

    {/* ══ CONTENT ══ */}
    <section className="py-20 px-6">
      <div className="max-w-3xl mx-auto space-y-10">
        {sections.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white border border-border rounded-2xl p-8 shadow-card relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
            <h2 className="font-figtree font-black text-lg text-bg-dark mb-3">{s.title}</h2>
            <p className="font-exo text-sm text-neutral-600 leading-relaxed">{s.body}</p>
          </motion.div>
        ))}

        <div className="pt-6 border-t border-border text-center">
          <p className="text-xs text-neutral-400 uppercase tracking-widest mb-4">
            Questions? Reach us directly.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="mailto:info@azuramobility.com"
              className="inline-flex items-center gap-2 bg-bg-dark text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-accent-amber hover:text-bg-dark transition-all shadow-capsule">
              info@azuramobility.com
            </a>
            <Link to="/invest"
              className="inline-flex items-center gap-2 border border-bg-dark/25 bg-white text-bg-dark px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-bg-dark transition-all">
              AZT Strategic Round ↗
            </Link>
          </div>
        </div>
      </div>
    </section>

  </div>
);

export default TermsConditions;
